/**
 * Header Scripts - Utility functions that need to be available early in the page lifecycle.
 * These functions are used by other scripts and components throughout the site.
 *
 * Configuration is passed via window.BnConfig which is set by the FTL template.
 */

declare global {
  interface Window {
    BnConfig: {
      showCookieConsentMessage: boolean;
    };
    HeaderScripts: typeof HeaderScripts;
    DeferredCookies: Record<string, () => void>;
    ListeningConsentUpdate: boolean;
    dataLayer: unknown[];
    YT: {
      PlayerState: {
        PLAYING: number;
        ENDED: number;
        PAUSED: number;
      };
    };
    ga?: {
      getAll: () => Array<{ get: (key: string) => string }>;
    };
  }
}

/**
 * Find a GET parameter value from the current URL
 */
function findGetParameter(parameterName: string): string {
  let result = "";
  const items = location.search.substr(1).split("&");
  for (let index = 0; index < items.length; index++) {
    const tmp = items[index].split("=");
    if (tmp[0] === parameterName) {
      result = decodeURIComponent(tmp[1]);
    }
  }
  return result;
}

/**
 * Find a cookie value by name
 */
function findCookieValue(cookieName: string): string {
  let result = "";
  const items = document.cookie.split("; ");
  for (let index = 0; index < items.length; index++) {
    const tmp = items[index].split("=");
    if (tmp[0] === cookieName) {
      result = tmp[1];
    }
  }
  return result;
}

/**
 * Get campaign parameter with priority: URL > Cookie > Page > DFM
 * Also handles cookie consent for setting cookies
 */
function getKampagnenParam(
  paramName: string,
  urlParam: string,
  pageParam: string | undefined,
  dfmParam: string,
  dfmId: string
): string {
  const cookieParam = findCookieValue(paramName);

  console.debug(
    "[%s] Resolving %s with values cookie=%s, url=%s, page=%s, dfm=%s",
    dfmId,
    paramName,
    cookieParam,
    urlParam,
    pageParam,
    dfmParam
  );

  if (urlParam.length > 0) {
    if (isCookieConsentGiven()) {
      console.debug(
        "[%s] Creating cookie with value from url '%s' and passing it to DFM.",
        dfmId,
        urlParam
      );
      document.cookie = paramName + "=" + urlParam + "; path=/; max-age=604800";
    } else {
      console.debug(
        "[%s] Deferr setting cookie and passing urlParam (%s) to DFM.",
        dfmId,
        urlParam
      );
      deferSettingCookie(paramName + "=" + urlParam + "; path=/; max-age=604800", function () {
        console.debug("[%s] Creating cookie with value from url '%s'", dfmId, urlParam);
      });
    }
    return urlParam;
  } else if (cookieParam.length > 0) {
    console.debug(
      "[%s] Cookie exists and no url param provided. Passing value from cookie '%s' to DFM.",
      dfmId,
      cookieParam
    );
    return cookieParam;
  } else if (pageParam !== undefined && pageParam.length > 0) {
    if (isCookieConsentGiven()) {
      console.debug(
        "[%s] Cookie does not exist and no url param provided. Creating cookie with value from page '%s' and passing it to DFM.",
        dfmId,
        pageParam
      );
      document.cookie = paramName + "=" + pageParam + "; path=/; max-age=604800";
    } else {
      console.debug(
        "[%s] Deferr setting cookie and passing pageParam %s to DFM.",
        dfmId,
        pageParam
      );
      deferSettingCookie(paramName + "=" + pageParam + "; path=/; max-age=604800", function () {
        console.debug(
          "[%s] Cookie does not exist and no url param provided. Creating cookie with value from page '%s'.",
          dfmId,
          pageParam
        );
      });
    }
    return pageParam;
  } else if (dfmParam.length > 0) {
    console.debug(
      "[%s] Cookie does not exist and no url or page param provided. Passing DFM value '%s' to DFM.",
      dfmId,
      dfmParam
    );
    return dfmParam;
  } else {
    console.debug(
      "[%s] Cookie does not exist and no url, page or dfm param provided. Passing empty string to DFM."
    );
    return "";
  }
}

/**
 * Check if cookie consent has been given
 * If showCookieConsentMessage is disabled, always returns true
 */
function isCookieConsentGiven(): boolean {
  if (window.BnConfig?.showCookieConsentMessage) {
    const consentStatus = findCookieValue("bnCookieConsentStatus");
    return (
      consentStatus !== undefined &&
      decodeURIComponent(consentStatus).match(/(denied|granted)/g) !== null
    );
  }
  return true;
}

/**
 * Check if the current device is a mobile device
 */
function isMobileDevice(): boolean {
  const navigators = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /iPad/i,
  ];

  for (let i = 0; i < navigators.length; i++) {
    if (navigator.userAgent.match(navigators[i])) {
      console.debug("Found mobile device.");
      return true;
    }
  }

  console.debug("Found desktop device.");
  return false;
}

/**
 * Get the brand name based on the current hostname
 */
function getBrandName(): string {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.indexOf("credit") > -1) {
    return "CREDIT-now";
  } else if (hostname.indexOf("lease") > -1) {
    return "LEASE-now";
  } else {
    return "BANK-now";
  }
}

/**
 * Get the Google Analytics client ID from the _ga cookie
 */
function getGoogleClientId(): string | undefined {
  if (!navigator.cookieEnabled) {
    return undefined;
  }

  // Primary method: Read GA4 client ID from _ga cookie
  const gaCookie = findCookieValue("_ga");
  if (gaCookie && gaCookie.indexOf(".") !== -1) {
    const parts = gaCookie.split(".");
    if (parts.length >= 4) {
      return parts[2] + "." + parts[3];
    }
  }

  // Fallback: Try Universal Analytics if still available (backward compatibility)
  if (typeof window.ga !== "undefined" && typeof window.ga.getAll !== "undefined") {
    try {
      return window.ga.getAll()[0].get("clientId");
    } catch (e) {
      console.debug("Universal Analytics not available:", e);
    }
  }

  return undefined;
}

/**
 * Get the ClickDimensions ID from the cuvid cookie
 */
function getClickDimensionsId(): string | undefined {
  if (findCookieValue("cuvid") !== "" && navigator.cookieEnabled) {
    return findCookieValue("cuvid");
  }
  return undefined;
}

/**
 * Defer setting a cookie until consent is given
 */
function deferSettingCookie(cookie: string, log: () => void): void {
  if (window.DeferredCookies === undefined) {
    window.DeferredCookies = {};
  }
  if (window.DeferredCookies[cookie] === undefined) {
    window.DeferredCookies[cookie] = log;
  }
}

/**
 * Listen for consent level changes and apply deferred cookies
 */
function listenForConsentLevelChange(): void {
  if (!window.BnConfig?.showCookieConsentMessage) {
    return;
  }

  if (window.ListeningConsentUpdate === true) {
    console.error("Change Listener already active. Early Return.");
    return;
  }

  if (window.ListeningConsentUpdate === undefined) {
    window.ListeningConsentUpdate = true;
  }

  if (!isCookieConsentGiven() && window.ListeningConsentUpdate) {
    const changeListenerInterval = setInterval(function () {
      if (isCookieConsentGiven() && window.DeferredCookies !== undefined) {
        Object.keys(window.DeferredCookies).forEach(function (key) {
          // call log function
          window.DeferredCookies[key]();
          // set cookie
          document.cookie = key;
        });
        clearInterval(changeListenerInterval);
        window.ListeningConsentUpdate = false;
      }
    }, 1000);
  }
}

/**
 * Post processes DFM properties which rely on runtime information and cannot be
 * post processed in the backend because of caching.
 * Also converts arrays that are passed as a string representation to real arrays.
 */
function dfmValuePostProcessor(key: string, value: string): string | string[] {
  // Polyfill for String.prototype.startsWith (IE support)
  if (!String.prototype.startsWith) {
    Object.defineProperty(String.prototype, "startsWith", {
      value: function (search: string, rawPos?: number) {
        const pos = rawPos && rawPos > 0 ? rawPos | 0 : 0;
        return this.substring(pos, pos + search.length) === search;
      },
    });
  }

  if (key === "uploadBaseUrl" && value.indexOf("/") === 0) {
    return window.location.origin + value;
  }

  if (value.startsWith("[") && value.endsWith("]")) {
    const elems = value.substring(1, value.length - 1).split(",");
    for (let i = 0; i < elems.length; i++) {
      elems[i] = elems[i].trim();
    }
    return elems;
  }

  return value;
}

/**
 * Push event into dataLayer to signal to the tags that the consent has been loaded
 */
function initGoogleConsentMode(): void {
  if (!window.BnConfig?.showCookieConsentMessage) {
    return;
  }

  // function defined per docs: https://developers.google.com/tag-platform/devguides/consent
  function gtag(...args: unknown[]): void {
    window.dataLayer.push(args);
  }

  /**
   * If the "bnCookieConsentStatus" cookie is set, parse its levels.
   * Set every level to "denied" if no "bnCookieConsentStatus" can be found.
   */
  function getGoogleConsentModeParams(): ["update", Record<string, string>] | undefined {
    const consentCookie = decodeURIComponent(findCookieValue("bnCookieConsentStatus"));
    if (consentCookie !== undefined && consentCookie.length && consentCookie.length > 0) {
      return [
        "update",
        consentCookie.split(",").reduce(function (acc: Record<string, string>, curr: string) {
          const keyVal = curr.split(":");
          acc[keyVal[0]] = keyVal[1];
          return acc;
        }, {}),
      ];
    }
    return undefined;
  }

  if (typeof window.dataLayer !== "undefined") {
    const consentParams = getGoogleConsentModeParams();
    if (consentParams && consentParams.length && consentParams[0] === "update") {
      gtag("consent", consentParams[0], consentParams[1]);
      if (consentParams[1]["ad_storage"] === "denied") {
        gtag("set", "ads_data_redaction", true);
      } else {
        gtag("set", "ads_data_redaction", false);
      }
      window.dataLayer.push({
        event: "consent_loaded",
      });
    }
  }
}

interface YouTubeEvent {
  target: {
    getDuration: () => number;
    getCurrentTime: () => number;
    getVideoData: () => { title: string };
    getVideoUrl: () => string;
  };
  data: number;
}

/**
 * Get a logger function for YouTube video events to push to dataLayer
 */
function getVideoDataLayerLogger(): (event: YouTubeEvent) => void {
  const percentageToLogToDataLayer = [10, 20, 30, 40, 50, 60, 70, 80, 90];

  let percentageLogTimer: ReturnType<typeof setInterval>;

  return function pushToDataLayer(event: YouTubeEvent): void {
    const player = event.target;
    let eventAction = "undefined"; // start|end|progress -- *** % (bspw. 10,20,30,...90 %)

    switch (event.data) {
      case window.YT.PlayerState.PLAYING:
        eventAction = "start";
        const totalVideoDuration = player.getDuration();
        const timeinMSForOnePercentage = (player.getDuration() * 1000) / 100;

        percentageLogTimer = setInterval(function () {
          const time = player.getCurrentTime();
          const playerTimeDifference = Math.round((time / totalVideoDuration) * 100);

          if (percentageToLogToDataLayer.includes(playerTimeDifference)) {
            window.dataLayer.push({
              event: "event",
              eventCategory: "video",
              eventAction: "progress -- " + playerTimeDifference + "%",
              eventLabel: event.target.getVideoData().title + " -- " + event.target.getVideoUrl(),
            });
          }
        }, timeinMSForOnePercentage);
        break;

      case window.YT.PlayerState.ENDED:
        eventAction = "end";
        clearInterval(percentageLogTimer);
        break;

      case window.YT.PlayerState.PAUSED:
        eventAction = "paused";
        clearInterval(percentageLogTimer);
        break;
      default:
        return;
    }

    window.dataLayer.push({
      event: "event",
      eventCategory: "video",
      eventAction: eventAction,
      eventLabel: event.target.getVideoData().title + " -- " + event.target.getVideoUrl(),
    });
  };
}

// Export all functions to the global HeaderScripts object
const HeaderScripts = {
  findGetParameter,
  findCookieValue,
  isMobileDevice,
  getKampagnenParam,
  getBrandName,
  getGoogleClientId,
  getClickDimensionsId,
  dfmValuePostProcessor,
  listenForConsentLevelChange,
  initGoogleConsentMode,
  getVideoDataLayerLogger,
};

window.HeaderScripts = HeaderScripts;

export { HeaderScripts };
